/**
 * Chemicals Configuration
 * Full management UI for the raw_materials catalog: table/grid view, search, CRUD.
 * Rendered by InventoryConfigs when configType === 'chemicals'.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../supabaseClient';
import {
  Box, Paper, Grid, Button, TextField, Typography, Card, CardContent, CardActions,
  IconButton, Chip, Stack, alpha, useTheme, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  ToggleButton, ToggleButtonGroup, InputAdornment, Tooltip, Select, MenuItem,
  FormControl, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  ViewList as ViewListIcon, ViewModule as ViewModuleIcon, Refresh as RefreshIcon,
  Warning as WarningIcon, Business as BusinessIcon, LocalOffer as LocalOfferIcon,
  Close as CloseIcon, KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import ChemicalsFormDialog from './chemicals-configs-components/ChemicalsFormDialog';
import ViewChemicalDialog from './chemicals-configs-components/ViewChemicalDialog';

export default function ChemicalsConfig({ companyId, suppliers = [], canEdit = true, canDelete = true }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [vendorDialogOpen,     setVendorDialogOpen]     = useState(false);
  const [vendorSearchTerm,     setVendorSearchTerm]     = useState('');
  const [vendorDialogSelected, setVendorDialogSelected] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('raw_materials')
        .select('*, supplier:supplier_id(id, name)')
        .eq('company_id', companyId)
        .order('name');
      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let result = items;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(m =>
        m.name?.toLowerCase().includes(q) || m.sku?.toLowerCase().includes(q) ||
        m.category?.toLowerCase().includes(q) || m.material_type?.toLowerCase().includes(q)
      );
    }
    if (vendorFilter) result = result.filter(m => m.supplier_id === vendorFilter);
    setFiltered(result);
    setPage(0);
  }, [items, search, vendorFilter]);

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const id = toDelete.id;
      await supabase.from('formulation_ingredients_new').delete().eq('ingredient_id', id);
      await supabase.from('batch_ingredients').delete().eq('raw_material_id', id);
      await supabase.from('product_raw_material_associations').delete().eq('raw_material_id', id);
      await supabase.from('raw_materials_inventory_items').delete().eq('raw_material_id', id);
      await supabase.from('raw_materials_transactions').delete().eq('raw_material_id', id);
      await supabase.from('raw_materials_inventory').delete().eq('raw_material_id', id);
      const { error } = await supabase.from('raw_materials').delete().eq('id', id);
      if (error) throw error;
      setDeleteOpen(false);
      setToDelete(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const hSx = {
    fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white',
    borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3
  };
  const rowSx = {
    bgcolor: 'white',
    transition: 'all 0.2s ease',
    '&:hover': { bgcolor: 'grey.50', transform: 'translateX(4px)', boxShadow: 'inset 4px 0 0 black' },
    '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`, py: 3, px: 3 }
  };
  const paged = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const renderTable = () => (
    <Paper sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`, overflow: 'hidden' }}>
      <TableContainer sx={{ overflowX: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={hSx}>Name</TableCell>
              <TableCell sx={hSx}>SKU</TableCell>
              <TableCell sx={hSx}>Unit Configuration</TableCell>
              <TableCell sx={hSx}>Type & Category</TableCell>
              <TableCell sx={hSx}>Vendor</TableCell>
              <TableCell sx={hSx}>Cost</TableCell>
              <TableCell sx={{ ...hSx, width: 140 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map(m => (
              <TableRow key={m.id} hover sx={{ ...rowSx, cursor: 'pointer' }} onClick={() => { setViewItem(m); setViewOpen(true); }}>
                <TableCell>
                  <Typography sx={{ fontWeight: 400, fontSize: '1.1rem', mb: 0.5 }}>{m.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{m.sku || '—'}</Typography>
                </TableCell>
                <TableCell>
                  {m.primary_unit_type && m.primary_unit_quantity && (
                    <Chip label={`${m.primary_unit_quantity}${m.primary_unit_measure || ''} per ${m.primary_unit_type}`} size="medium" variant="outlined" color="primary" sx={{ fontWeight: 500, fontSize: '0.85rem', height: 32 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {m.category && <Chip label={m.category} size="medium" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.85rem', height: 32 }} />}
                    {m.material_type && <Chip label={m.material_type} size="medium" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.85rem', height: 32 }} />}
                    {m.form && <Chip label={m.form} size="medium" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.85rem', height: 32 }} />}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>{m.supplier?.name || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.unit_cost ? `$${parseFloat(m.unit_cost).toFixed(2)}` : '—'}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <IconButton size="medium" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(m); setFormOpen(true); }}
                      sx={{ color: 'primary.main', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: 2, transition: 'all 0.2s', '&:hover': { bgcolor: 'primary.main', color: 'white', transform: 'scale(1.05)' } }}>
                      <EditIcon fontSize="medium" />
                    </IconButton>
                    <IconButton size="medium" disabled={!canDelete} onClick={(e) => { e.stopPropagation(); setToDelete(m); setDeleteOpen(true); }}
                      sx={{ color: 'error.main', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: 2, transition: 'all 0.2s', '&:hover': { bgcolor: 'error.main', color: 'white', transform: 'scale(1.05)' } }}>
                      <DeleteIcon fontSize="medium" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {paged.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                  <Typography color="text.secondary">{items.length === 0 ? 'No chemicals yet. Add your first chemical.' : 'No chemicals match your search.'}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderCard = (m) => (
    <Card key={m.id} onClick={() => { setViewItem(m); setViewOpen(true); }} sx={{ height: '100%', borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white', boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`, transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', cursor: 'pointer', '&:hover': { transform: 'translateY(-8px)', boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.3)}`, border: `1px solid ${theme.palette.primary.main}` } }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, fontSize: '1.3rem', lineHeight: 1.2 }}>{m.name}</Typography>
        {m.sku && <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>SKU: {m.sku}</Typography>}
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {m.primary_unit_quantity && <Chip label={`${m.primary_unit_quantity}${m.primary_unit_measure || ''} per ${m.primary_unit_type}`} size="medium" variant="outlined" color="primary" sx={{ fontWeight: 500, fontSize: '0.85rem', height: 32 }} />}
          {m.category && <Chip label={m.category} size="medium" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.85rem', height: 32 }} />}
          {m.material_type && <Chip label={m.material_type} size="medium" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.85rem', height: 32 }} />}
        </Stack>
        {m.supplier && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}><BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{m.supplier.name}</Typography></Box>}
        {m.unit_cost && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><LocalOfferIcon sx={{ fontSize: 20, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>${parseFloat(m.unit_cost).toFixed(2)} per {m.primary_unit_measure || 'unit'}</Typography></Box>}
      </CardContent>
      <CardActions sx={{ p: 3, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" size="medium" startIcon={<EditIcon />} disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(m); setFormOpen(true); }} sx={{ borderRadius: 2, px: 2, py: 1, fontWeight: 500 }}>Edit</Button>
        <Button variant="outlined" size="medium" color="error" startIcon={<DeleteIcon />} disabled={!canDelete} onClick={(e) => { e.stopPropagation(); setToDelete(m); setDeleteOpen(true); }} sx={{ borderRadius: 2, px: 2, py: 1, fontWeight: 500 }}>Delete</Button>
      </CardActions>
    </Card>
  );


  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, p: 4, borderRadius: 4, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white', boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`, transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}` } }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '1.8rem', mb: 1 }}>Chemicals Configuration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: '500px' }}>
            Manage chemicals catalog, track specifications, vendor information, and unit configurations
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`${items.length} Chemicals`} size="small" sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }} />
            <Chip label={`${suppliers.length} Vendors`} size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Tooltip title="Refresh chemicals data">
            <IconButton onClick={load} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, color: 'primary.main' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => { setEditing(null); setFormOpen(true); }}
            sx={{ borderRadius: 2, px: 3, py: 1.2, fontWeight: 500, boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`, '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500', boxShadow: 'none' } }}>
            Add Chemical
          </Button>
        </Box>
      </Box>

      {/* Search & Filters */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`, boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}` }}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>Search</Typography>
            <TextField fullWidth placeholder="Search chemicals..." value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: theme.palette.primary.main }} /></InputAdornment>, sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white', height: 56 } }}
              sx={{ '& .MuiOutlinedInput-root': { height: 56, '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } } }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>Vendor</Typography>
            <Button fullWidth variant="outlined" endIcon={<ArrowDownIcon />}
              onClick={() => { setVendorDialogOpen(true); setVendorSearchTerm(''); setVendorDialogSelected(vendorFilter ? suppliers.find(s => s.id === vendorFilter) || null : null); }}
              sx={{ height: 56, borderRadius: 3, textTransform: 'none', justifyContent: 'space-between', fontWeight: 500,
                bgcolor: vendorFilter ? alpha(theme.palette.primary.main, 0.08) : 'white',
                border: vendorFilter ? `1px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                color: vendorFilter ? 'primary.main' : 'text.secondary',
                '&:hover': { border: `1px solid ${theme.palette.primary.main}`, bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
              {vendorFilter ? suppliers.find(s => s.id === vendorFilter)?.name || 'Vendor' : 'All Vendors'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>View Mode</Typography>
            <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)}
              sx={{ height: 56, gap: 1, '& .MuiToggleButton-root': { borderRadius: 3, px: 3, py: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, '&.Mui-selected': { bgcolor: theme.palette.grey[200], color: 'text.primary' } } }}>
              <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
              <ToggleButton value="table"><ViewListIcon /></ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>{filtered.length}</Typography>
              <Typography variant="body2" sx={{ color: 'black', fontWeight: 500, fontSize: '0.9rem' }}>found</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Items Display */}
      {viewMode === 'grid' ? (
        <Grid container spacing={4}>
          {paged.map(m => <Grid item xs={12} sm={6} md={4} lg={3} key={m.id}>{renderCard(m)}</Grid>)}
          {paged.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ textAlign: 'center', py: 8, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                  {items.length === 0 ? 'No Chemicals Yet' : 'No Matching Chemicals'}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : renderTable()}

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[12, 24, 48]} />
        </Paper>
      </Box>

      {/* Form Dialog */}
      <ChemicalsFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); load(); }}
        editingItem={editing}
        companyId={companyId}
        suppliers={suppliers}
      />

      {/* View Dialog */}
      <ViewChemicalDialog
        open={viewOpen}
        onClose={() => { setViewOpen(false); setViewItem(null); }}
        item={viewItem}
        onEdit={(item) => { setViewOpen(false); setViewItem(null); setEditing(item); setFormOpen(true); }}
      />

      {/* Vendor Filter Dialog */}
      <Dialog open={vendorDialogOpen} onClose={() => setVendorDialogOpen(false)} fullWidth maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 4, height: '80vh', maxHeight: '80vh', margin: 'auto',
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, boxShadow: '0 24px 48px rgba(0,0,0,0.15)' } }}>
        <DialogTitle sx={{ pb: 2, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>Select Vendor</Typography>
          <IconButton onClick={() => setVendorDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField fullWidth placeholder="Search vendors..." value={vendorSearchTerm}
              onChange={(e) => setVendorSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: theme.palette.primary.main }} /></InputAdornment>,
                sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white',
                  '&:hover': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}` } } }}
              sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } } }} />
          </Box>
          <Box sx={{ overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Vendor</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.filter(s => !vendorSearchTerm || s.name.toLowerCase().includes(vendorSearchTerm.toLowerCase())).map(s => (
                  <TableRow key={s.id} hover sx={{ cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.50', transform: 'translateX(4px)', boxShadow: 'inset 4px 0 0 black' },
                    '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`, py: 2, pl: 3, pr: 3 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>{s.name}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined" onClick={() => setVendorDialogSelected(s)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', px: 2, py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: vendorDialogSelected?.id === s.id ? 'black' : 'white',
                          color: vendorDialogSelected?.id === s.id ? 'white' : 'text.primary',
                          '&:hover': { border: `1px solid ${theme.palette.primary.main}`, transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            bgcolor: vendorDialogSelected?.id === s.id ? 'black' : 'white',
                            color: vendorDialogSelected?.id === s.id ? 'white' : 'text.primary' } }}>
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, background: alpha(theme.palette.grey[50], 0.5) }}>
          <Button onClick={() => { setVendorFilter(''); setVendorDialogOpen(false); }} sx={{ borderRadius: 2, px: 3 }}>Clear Filter</Button>
          <Button onClick={() => setVendorDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" onClick={() => { setVendorFilter(vendorDialogSelected?.id || ''); setVendorDialogOpen(false); }}
            sx={{ borderRadius: 2, px: 3, bgcolor: 'black', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pb: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), display: 'flex', alignItems: 'center' }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>Delete Chemical</Typography>
            <Typography variant="body2" color="text.secondary">This will remove the chemical and all related inventory data.</Typography>
          </Box>
          <IconButton onClick={() => setDeleteOpen(false)} disabled={deleting} sx={{ mt: -1, mr: -1 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {toDelete && (
            <Paper elevation={0} sx={{ p: 2.5, bgcolor: alpha(theme.palette.error.main, 0.04), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 600, mb: 1 }}>{toDelete.name}</Typography>
              {toDelete.sku && <Typography variant="body2">SKU: {toDelete.sku}</Typography>}
            </Paper>
          )}
          <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`, borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningIcon sx={{ color: 'error.main', mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">This action cannot be undone. All inventory records, transactions, and related data will be permanently deleted.</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ borderRadius: 2, px: 3 }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
